import {
  errorResponseSchema,
  healthResponseSchema,
  type HealthResponse,
} from '@gamble-chess/contracts';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { z } from 'zod';

export class ApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

/**
 * Turns a failed response into an `ApiError`, keeping the server's explanation when it sent
 * one in the shared error shape, and `fallback` otherwise.
 */
export async function toApiError(response: Response, fallback: string): Promise<ApiError> {
  const body: unknown = await response.json().catch(() => undefined);
  const parsed = errorResponseSchema.safeParse(body);
  return new ApiError(parsed.success ? parsed.data.message : fallback, response.status);
}

export interface RequestOptions {
  readonly method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  /** Sent as JSON. */
  readonly body?: unknown;
  readonly signal?: AbortSignal | undefined;
}

async function send(url: string, failure: string, options: RequestOptions): Promise<Response> {
  const init: RequestInit = { method: options.method ?? 'GET' };
  if (options.body !== undefined) {
    init.headers = { 'content-type': 'application/json' };
    init.body = JSON.stringify(options.body);
  }
  if (options.signal !== undefined) {
    init.signal = options.signal;
  }
  const response = await fetch(url, init);
  if (!response.ok) {
    throw await toApiError(response, failure);
  }
  return response;
}

/**
 * Calls the API and parses the answer through its contract. A failure becomes an `ApiError`
 * with the server's explanation, or `failure` when it sent none.
 */
export async function requestJson<T>(
  url: string,
  schema: z.ZodType<T>,
  failure: string,
  options: RequestOptions = {},
): Promise<T> {
  const response = await send(url, failure, options);
  return schema.parse(await response.json());
}

/** Like `requestJson`, for routes that answer 204 without a body. */
export async function requestNoContent(
  url: string,
  failure: string,
  options: RequestOptions = {},
): Promise<void> {
  await send(url, failure, options);
}

export async function fetchHealth(signal?: AbortSignal): Promise<HealthResponse> {
  const response = await fetch('/api/health', signal === undefined ? {} : { signal });

  // 503 still carries a valid health payload describing what is degraded.
  if (!response.ok && response.status !== 503) {
    throw new ApiError(`Health check failed with ${String(response.status)}`, response.status);
  }

  return healthResponseSchema.parse(await response.json());
}

export function useHealth(): UseQueryResult<HealthResponse> {
  return useQuery({
    queryKey: ['health'],
    queryFn: ({ signal }) => fetchHealth(signal),
    refetchInterval: 30_000,
  });
}
